/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * "All partners" header menu (main domain only): a dropdown with a search box that
 * filters the partner directory by company name; picking a partner navigates to
 * that company's storefront domain.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, Store, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface Partner {
  name: string;
  host: string;
}

export default function PartnersMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // Prefetch the directory on mount so the dropdown opens instantly.
  useEffect(() => {
    setLoading(true);
    api
      .getPartners()
      .then(setPartners)
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = () => setOpen((o) => !o);

  const filtered = (partners ?? []).filter((p) =>
    p.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div ref={rootRef} className="relative hidden sm:block">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-1 hover:underline"
      >
        {t('partners.all')} <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 surface rounded-xl shadow-2xl p-2 z-50">
          <div className="relative mb-2">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('partners.searchPlaceholder')}
              className="w-full pl-8 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2"
              style={{ outlineColor: 'var(--color-primary)' }}
            />
          </div>

          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm opacity-70">
                <Loader2 size={16} className="animate-spin" /> {t('partners.loading')}
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-sm opacity-60">
                {partners && partners.length > 0 ? t('partners.noMatch') : t('partners.none')}
              </div>
            ) : (
              filtered.map((p) => (
                <a
                  key={p.host}
                  href={`https://${p.host}`}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Store size={16} className="shrink-0 opacity-70" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold truncate">{p.name}</span>
                    <span className="block text-xs opacity-60 truncate">{p.host}</span>
                  </span>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
